import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRepository } from '../data/repository';
import type {
  Profile,
  ReferenceBlock as RefBlockType,
  FinancialYear,
  Document,
  ActivityRecord,
  Relationship,
  StagedDocument,
} from '../types/vault';
import { ReferenceBlock, ReferenceBlockForm } from '../components/ReferenceBlock';
import { ModalDrawer } from '../components/ModalDrawer';
import { ProfileForm } from '../components/ProfileForm';
import { FinancialYearForm } from '../components/FinancialYearForm';
import { DocumentPreview, DocumentHoverPreview } from '../components/DocumentPreview';
import { TaxTrendSection } from '../components/TaxTrendSection';
import { formatFY, formatAUD } from '../utils/clipboard';
import { formatAustralianDateTime } from '../components/SmartDateField';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiEdit2,
  FiPlus,
  FiFileText,
  FiClock,
  FiChevronLeft,
  FiAlertCircle,
  FiRefreshCw,
  FiUsers,
  FiEye,
  FiDownload,
  FiLock,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
} = FiIcons;

export function ProfileDetail() {
  const { profileId } = useParams<{ profileId: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [blocks, setBlocks] = useState<RefBlockType[]>([]);
  const [years, setYears] = useState<FinancialYear[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [activity, setActivity] = useState<ActivityRecord[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingBlock, setEditingBlock] = useState<RefBlockType | null>(null);
  const [isAddingBlock, setIsAddingBlock] = useState(false);
  const [isAddingYear, setIsAddingYear] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [hoverPreview, setHoverPreview] = useState<{
    docId: string;
    anchorRect: DOMRect;
    isPinned: boolean;
  } | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [expandedYearDocuments, setExpandedYearDocuments] = useState<string | null>(null);
  const [expandedYearDetails, setExpandedYearDetails] = useState<string | null>(null);
  const previewOpenTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const previewCloseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const supportsHoverRef = useRef(
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const role = getRepository().getCurrentUser()?.role || 'read_only';
  const canWrite = role === 'owner' || role === 'member';

  const loadData = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setError(null);
    const repo = getRepository();
    try {
      const [p, b, y, d, a, rels, allP] = await Promise.all([
        repo.getProfile(profileId),
        repo.listReferenceBlocks(profileId),
        repo.listFinancialYears(profileId),
        repo.listDocuments({ profileId }),
        repo.listActivityRecords(profileId),
        repo.listRelationships(profileId),
        repo.listProfiles(),
      ]);
      setProfile(p);
      setBlocks(b);
      setYears(y);
      setDocuments(d);
      setActivity(a);
      setRelationships(rels);
      setAllProfiles(allP);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load profile data');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('mfv:documents-changed', handler);
    return () => window.removeEventListener('mfv:documents-changed', handler);
  }, [loadData]);

  useEffect(() => {
    return () => {
      clearTimeout(previewOpenTimerRef.current);
      clearTimeout(previewCloseTimerRef.current);
    };
  }, []);

  const toggleYearDetails = (yearId: string) => {
    if (expandedYearDetails === yearId) {
      setExpandedYearDetails(null);
      if (expandedYearDocuments === yearId) {
        setExpandedYearDocuments(null);
      }
    } else {
      setExpandedYearDetails(yearId);
    }
  };

  const toggleYearDocuments = (yearId: string) => {
    setExpandedYearDocuments(prev => (prev === yearId ? null : yearId));
  };

  const handleSaveWithDocuments = async (
    year: FinancialYear,
    staged: StagedDocument[]
  ): Promise<{ year: FinancialYear; results: StagedDocument[] }> => {
    const savedYear = await getRepository().saveFinancialYear(year);
    if (!savedYear?.id) throw new Error('Financial year save did not return a valid record.');
    if (!profile) throw new Error('Profile not loaded.');
    const itemsToUpload: StagedDocument[] = staged.map(item => ({
      ...item,
      profileId: profile.id,
      financialYearId: savedYear.id,
      tags: Array.from(new Set([...(item.tags || []), `FY${savedYear.endingYear}`])),
    }));
    const results: StagedDocument[] = [];
    const nonDuplicate: StagedDocument[] = [];
    for (const item of itemsToUpload) {
      try {
        const dupResult = await getRepository().checkDocumentDuplicates(item);
        if (dupResult.isDuplicate) {
          results.push({
            ...item,
            status: 'failed' as const,
            error: `Possible duplicate: ${dupResult.existingDocumentTitle || 'existing document'}. Remove this item or use the full Documents uploader to add it as a version.`,
          });
        } else {
          nonDuplicate.push(item);
        }
      } catch {
        nonDuplicate.push(item);
      }
    }
    if (nonDuplicate.length > 0) {
      try {
        const uploadResults = await getRepository().uploadDocuments(nonDuplicate);
        results.push(...uploadResults);
      } catch (err) {
        results.push(
          ...nonDuplicate.map(item => ({
            ...item,
            status: 'failed' as const,
            error: err instanceof Error ? err.message : 'Upload failed. Please retry.',
          }))
        );
      }
    }
    const hasSuccess = results.some(result => result.status === 'uploaded');
    if (hasSuccess) {
      window.dispatchEvent(new CustomEvent('mfv:documents-changed'));
    }
    await loadData();
    return { year: savedYear, results };
  };

  const handleEyeHoverEnter = (docId: string, e: React.MouseEvent) => {
    if (!supportsHoverRef.current) return;
    clearTimeout(previewCloseTimerRef.current);
    clearTimeout(previewOpenTimerRef.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    previewOpenTimerRef.current = setTimeout(() => {
      setHoverPreview({ docId, anchorRect: rect, isPinned: false });
    }, 500);
  };
  const handleEyeHoverLeave = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };
  const handleEyeFocus = (docId: string, e: React.FocusEvent) => {
    if (!supportsHoverRef.current) return;
    clearTimeout(previewCloseTimerRef.current);
    clearTimeout(previewOpenTimerRef.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoverPreview({ docId, anchorRect: rect, isPinned: false });
  };
  const handleEyeBlur = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };
  const keepHoverPreviewOpen = () => { clearTimeout(previewCloseTimerRef.current); };
  const scheduleHoverPreviewClose = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };
  const toggleHoverPreviewPin = () => {
    setHoverPreview(prev => (prev ? { ...prev, isPinned: !prev.isPinned } : null));
  };
  const closeHoverPreview = () => {
    clearTimeout(previewOpenTimerRef.current);
    clearTimeout(previewCloseTimerRef.current);
    setHoverPreview(null);
  };
  const openHoverPreviewFull = () => {
    setHoverPreview(prev => {
      if (prev) setPreviewDocId(prev.docId);
      return null;
    });
    clearTimeout(previewOpenTimerRef.current);
    clearTimeout(previewCloseTimerRef.current);
  };

  const handleDownload = async (docId: string) => {
    setDownloadError(null);
    try {
      const { blob, filename } = await getRepository().downloadDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const getYearLabel = (id?: string) => {
    if (!id) return '';
    const year = years.find(y => y.id === id);
    return year ? `FY${year.endingYear}` : '';
  };

  const getDocumentsForYear = (financialYearId: string) =>
    documents
      .filter(d => !d.archived && d.financialYearId === financialYearId)
      .sort((a, b) => a.title.localeCompare(b.title));

  if (loading)
    return (
      <div className="p-8 animate-pulse space-y-6">
        <div className="h-6 bg-slate-200 rounded w-1/3" />
        <div className="h-64 bg-slate-200 rounded-2xl" />
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-400" />
        <h2 className="text-xl font-semibold text-slate-900">Unable to Load Profile</h2>
        <p className="text-slate-500 max-w-sm">{error}</p>
        <button
          onClick={loadData}
          className="inline-flex items-center px-4 py-2 bg-[#0B2138] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );

  if (!profile)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-900">Profile Not Found</h2>
        <p className="text-slate-500 max-w-sm">
          The profile you requested does not exist or has been removed.
        </p>
        <Link to="/profiles" className="text-sm font-medium text-[#0F766E] hover:underline">
          Back to Profiles
        </Link>
      </div>
    );

  return (
    <div className="space-y-6">
      <Link
        to="/profiles"
        className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0F766E]"
      >
        <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
        Back to Profiles
      </Link>

      <header
        className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
            {profile.displayName}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#0F766E]/10 text-[#0F766E]"
            >
              {profile.profileType}
            </span>
            {profile.legalName && (
              <span className="text-sm text-slate-500">{profile.legalName}</span>
            )}
          </div>
          {profile.aliases && profile.aliases.length > 0 && (
            <p className="text-xs text-slate-400 mt-1">
              Also known as: {profile.aliases.join(',')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <Link
            to={`/reports?profileId=${profile.id}`}
            className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0F766E] whitespace-nowrap min-h-[44px]"
          >
            <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
            Export Report
          </Link>
          {canWrite && (
            <button
              onClick={() => setEditingProfile(true)}
              className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0F766E] whitespace-nowrap min-h-[44px]"
            >
              <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-2" />
              Edit Profile
            </button>
          )}
        </div>
      </header>

      {downloadError && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between">
          <p className="text-sm text-red-700">{downloadError}</p>
          <button
            onClick={() => setDownloadError(null)}
            className="text-xs text-red-600 hover:text-red-800 font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {years.filter(year => !year.archived).length >= 2 && (
        <TaxTrendSection
          profileName={profile.displayName}
          years={years}
          showSummaryCards={true}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Reference Blocks */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-slate-900">Reference Blocks</h2>
              {canWrite && (
                <button
                  onClick={() => setIsAddingBlock(true)}
                  className="text-sm font-medium text-[#0F766E] hover:text-[#0B2138] flex items-center"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                  Add Block
                </button>
              )}
            </div>
            {blocks.length === 0 ? (
              <div className="py-8 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
                No reference blocks added yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {blocks.map(block => (
                  <ReferenceBlock
                    key={block.id}
                    block={block}
                    onEdit={canWrite ? () => setEditingBlock(block) : undefined}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Financial Years — compact accordion rows */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-slate-900">Financial Years</h2>
              {canWrite && (
                <button
                  onClick={() => setIsAddingYear(true)}
                  className="text-sm font-medium text-[#0F766E] hover:text-[#0B2138] flex items-center"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                  Add Year
                </button>
              )}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden relative">
              <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-100 hidden sm:block" />
              <div className="divide-y divide-slate-50">
                {years.map(year => {
                  const yearDocs = getDocumentsForYear(year.id);
                  const isDetailsExpanded = expandedYearDetails === year.id;
                  const isDocsExpanded = expandedYearDocuments === year.id;
                  let outcomeLabel: string;
                  let outcomeValue: string;
                  let outcomeColor: string;
                  if ((year.taxRefund ?? 0) > 0) {
                    outcomeLabel = 'Refund';
                    outcomeValue = formatAUD(year.taxRefund);
                    outcomeColor = 'text-green-600';
                  } else if ((year.taxPayable ?? 0) > 0) {
                    outcomeLabel = 'Payable';
                    outcomeValue = formatAUD(year.taxPayable);
                    outcomeColor = 'text-amber-600';
                  } else {
                    outcomeLabel = 'Outcome';
                    outcomeValue = 'No Balance';
                    outcomeColor = 'text-slate-400';
                  }
                  return (
                    <div key={year.id} className="relative">
                      {/* Collapsed row */}
                      <div
                        className="py-3 sm:pl-12 hover:bg-slate-50/50 transition-colors"
                      >
                        <div className="absolute left-6 -translate-x-1/2 top-3.5 w-3 h-3 rounded-full border-2 border-white bg-[#0F766E] hidden sm:block" />
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          {/* FY + status */}
                          <div className="flex items-center gap-2 shrink-0">
                            <h3 className="text-sm font-semibold text-slate-900">
                              {formatFY(year.endingYear)}
                            </h3>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                year.status === 'Finalised'
                                  ? 'bg-[#0F766E]/10 text-[#0F766E]'
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {year.status}
                            </span>
                          </div>

                          {/* Desktop inline metrics (lg+) */}
                          <div className="hidden lg:flex items-center gap-3 text-xs flex-1 min-w-0">
                            <span className="text-slate-400 shrink-0">
                              Gross{' '}
                              <span className="font-medium text-slate-700">
                                {formatAUD(year.grossIncome)}
                              </span>
                            </span>
                            <span className="text-slate-400 shrink-0">
                              Taxable{' '}
                              <span className="font-medium text-slate-700">
                                {formatAUD(year.taxableIncome)}
                              </span>
                            </span>
                            <span className="text-slate-400 shrink-0">
                              Withheld{' '}
                              <span className="font-medium text-slate-700">
                                {formatAUD(year.taxWithheld)}
                              </span>
                            </span>
                            <span className="shrink-0">
                              <span className="text-slate-400">{outcomeLabel} </span>
                              <span className={`font-medium ${outcomeColor}`}>
                                {outcomeValue}
                              </span>
                            </span>
                          </div>

                          {/* Mobile/tablet key metrics (below lg) */}
                          <div className="flex lg:hidden items-center gap-2 text-xs">
                            <span className="text-slate-400 shrink-0">
                              Taxable{' '}
                              <span className="font-medium text-slate-700">
                                {formatAUD(year.taxableIncome)}
                              </span>
                            </span>
                            <span className="shrink-0">
                              <span className="text-slate-400">{outcomeLabel} </span>
                              <span className={`font-medium ${outcomeColor}`}>
                                {outcomeValue}
                              </span>
                            </span>
                          </div>

                          {/* Right: doc count, View Details, expand */}
                          <div className="flex items-center gap-2 sm:ml-auto shrink-0 mt-0.5 sm:mt-0">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" />
                              {yearDocs.length}
                            </span>
                            <Link
                              to={`/profiles/${profile.id}/financial-years/${year.id}`}
                              className="px-2 py-1 text-[11px] font-medium text-[#0F766E] hover:text-[#0B2138] hover:bg-[#0F766E]/5 rounded transition-colors"
                            >
                              View Details
                            </Link>
                            <button
                              onClick={() => toggleYearDetails(year.id)}
                              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors"
                              aria-label={
                                isDetailsExpanded
                                  ? `Collapse FY${year.endingYear} details`
                                  : `Expand FY${year.endingYear} details`
                              }
                            >
                              <SafeIcon
                                icon={isDetailsExpanded ? FiChevronUp : FiChevronDown}
                                className="w-4 h-4"
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isDetailsExpanded && (
                        <div className="sm:pl-12 pb-3 pt-1 border-t border-slate-100/60">
                          {/* Compact metric grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                            <div className="bg-slate-50/70 rounded px-2.5 py-1.5">
                              <p className="text-[10px] text-slate-500">Gross Income</p>
                              <p className="text-sm font-medium text-slate-900">
                                {formatAUD(year.grossIncome)}
                              </p>
                            </div>
                            <div className="bg-slate-50/70 rounded px-2.5 py-1.5">
                              <p className="text-[10px] text-slate-500">Taxable Income</p>
                              <p className="text-sm font-medium text-slate-900">
                                {formatAUD(year.taxableIncome)}
                              </p>
                            </div>
                            <div className="bg-slate-50/70 rounded px-2.5 py-1.5">
                              <p className="text-[10px] text-slate-500">Tax Withheld</p>
                              <p className="text-sm font-medium text-slate-900">
                                {formatAUD(year.taxWithheld)}
                              </p>
                            </div>
                            <div className="bg-slate-50/70 rounded px-2.5 py-1.5">
                              <p className="text-[10px] text-slate-500">{outcomeLabel}</p>
                              <p className={`text-sm font-medium ${outcomeColor}`}>
                                {outcomeValue}
                              </p>
                            </div>
                          </div>

                          {/* Document section */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <SafeIcon icon={FiFileText} className="w-4 h-4" />
                              {yearDocs.length === 0 ? (
                                <span>No documents linked to this financial year</span>
                              ) : (
                                <span>Documents ({yearDocs.length})</span>
                              )}
                            </div>
                            {yearDocs.length > 0 && (
                              <button
                                onClick={() => toggleYearDocuments(year.id)}
                                aria-expanded={isDocsExpanded}
                                aria-label={`${isDocsExpanded ? 'Hide' : 'Show'} documents for ${formatFY(year.endingYear)}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#0F766E] hover:bg-[#0F766E]/5 rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors"
                              >
                                {isDocsExpanded ? 'Hide Documents' : 'Show Documents'}
                                <SafeIcon
                                  icon={isDocsExpanded ? FiChevronUp : FiChevronDown}
                                  className="w-3.5 h-3.5"
                                />
                              </button>
                            )}
                          </div>

                          {/* Expanded document list */}
                          {isDocsExpanded && yearDocs.length > 0 && (
                            <div className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
                              {yearDocs.map(doc => (
                                <div
                                  key={doc.id}
                                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                  <div
                                    className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center text-slate-400 shrink-0 border border-slate-100"
                                  >
                                    <SafeIcon icon={FiFileText} className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <button
                                      onClick={() => setPreviewDocId(doc.id)}
                                      className="text-sm font-medium text-slate-900 truncate hover:text-[#0F766E] text-left w-full focus:outline-none focus:underline"
                                    >
                                      {doc.title}
                                    </button>
                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap mt-0.5">
                                      <span>{doc.documentType}</span>
                                      {doc.final ? (
                                        <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                                          <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 mr-0.5 inline" />
                                          Final
                                        </span>
                                      ) : (
                                        <span className="px-1 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">
                                          Draft
                                        </span>
                                      )}
                                      {doc.sensitive && (
                                        <span className="px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">
                                          <SafeIcon icon={FiLock} className="w-2.5 h-2.5 mr-0.5 inline" />
                                          Sensitive
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      onClick={() => {
                                        setPreviewDocId(doc.id);
                                        closeHoverPreview();
                                      }}
                                      onMouseEnter={e => handleEyeHoverEnter(doc.id, e)}
                                      onMouseLeave={handleEyeHoverLeave}
                                      onFocus={e => handleEyeFocus(doc.id, e)}
                                      onBlur={handleEyeBlur}
                                      aria-label={`Preview ${doc.title}`}
                                      className="p-1.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-2"
                                      style={{ color: 'var(--mfv-teal)' }}
                                    >
                                      <SafeIcon icon={FiEye} className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDownload(doc.id)}
                                      aria-label={`Download ${doc.title}`}
                                      className="p-1.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-2"
                                      style={{ color: 'var(--mfv-text-muted)' }}
                                    >
                                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {years.length === 0 && (
                  <div className="p-6 text-center text-slate-500 text-sm">
                    No financial years recorded.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          {/* Relationships */}
          {relationships.length > 0 && (
            <section className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h2 className="text-sm font-medium text-slate-900 mb-4 flex items-center">
                <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-slate-400" />
                Related Profiles
              </h2>
              <div className="space-y-2">
                {relationships.map(rel => {
                  const relatedId =
                    rel.fromProfileId === profile.id ? rel.toProfileId : rel.fromProfileId;
                  const related = allProfiles.find(p => p.id === relatedId);
                  return (
                    <Link
                      key={rel.id}
                      to={`/profiles/${relatedId}`}
                      className="flex items-center p-2 -mx-2 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                    >
                      <div
                        className="w-8 h-8 rounded bg-[#102D4C]/5 flex items-center justify-center text-[#102D4C] shrink-0 mr-3 border border-[#102D4C]/10"
                      >
                        <SafeIcon icon={FiIcons.FiUser} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {related?.displayName ?? relatedId}
                        </p>
                        <p className="text-xs text-slate-500">{rel.relationshipType}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Profile Documents */}
          <section className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-slate-900 flex items-center">
                <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2 text-slate-400" />
                Documents ({documents.length})
              </h2>
              {documents.length > 0 && (
                <Link
                  to={`/documents?profile=${profile.id}`}
                  className="text-xs font-medium text-[#0F766E] hover:text-[#0B2138]"
                >
                  View All →
                </Link>
              )}
            </div>
            {documents.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">
                No documents associated with this profile.
              </p>
            ) : (
              <div className="space-y-2">
                {documents.slice(0, 6).map(doc => {
                  const yLabel = getYearLabel(doc.financialYearId);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div
                        className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center text-slate-400 shrink-0 border border-slate-100"
                      >
                        <SafeIcon icon={FiFileText} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <button
                          onClick={() => setPreviewDocId(doc.id)}
                          className="text-sm font-medium text-slate-900 truncate hover:text-[#0F766E] text-left w-full"
                        >
                          {doc.title}
                        </button>
                        <div className="flex items-center gap-1 text-xs text-slate-500 flex-wrap">
                          <span>{doc.documentType}</span>
                          {yLabel && <span>• {yLabel}</span>}
                          {doc.final ? (
                            <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                              <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 mr-0.5 inline" />
                              Final
                            </span>
                          ) : (
                            <span className="px-1 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">
                              Draft
                            </span>
                          )}
                          {doc.sensitive && (
                            <span className="px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">
                              <SafeIcon icon={FiLock} className="w-2.5 h-2.5 mr-0.5 inline" />
                              Sensitive
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => {
                            setPreviewDocId(doc.id);
                            closeHoverPreview();
                          }}
                          onMouseEnter={e => handleEyeHoverEnter(doc.id, e)}
                          onMouseLeave={handleEyeHoverLeave}
                          onFocus={e => handleEyeFocus(doc.id, e)}
                          onBlur={handleEyeBlur}
                          aria-label={`Preview ${doc.title}`}
                          className="p-1.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-2"
                          style={{ color: 'var(--mfv-teal)' }}
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc.id)}
                          aria-label={`Download ${doc.title}`}
                          className="p-1.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-2"
                          style={{ color: 'var(--mfv-text-muted)' }}
                        >
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Recent Activity */}
          <section className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-sm font-medium text-slate-900 mb-4 flex items-center">
              <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-slate-400" />
              Recent Activity
            </h2>
            <div className="space-y-4">
              {activity.slice(0, 5).map(act => (
                <div key={act.id} className="relative pl-4 border-l-2 border-slate-100">
                  <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-slate-300" />
                  <p className="text-sm text-slate-700">{act.description}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {formatAustralianDateTime(act.created)}
                  </p>
                </div>
              ))}
              {activity.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-2">No recent activity.</p>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Hover Preview */}
      {hoverPreview &&
        (() => {
          const doc = documents.find(d => d.id === hoverPreview.docId);
          if (!doc) return null;
          return (
            <DocumentHoverPreview
              document={doc}
              visible={true}
              anchorRect={hoverPreview.anchorRect}
              profileName={profile?.displayName}
              yearLabel={getYearLabel(doc.financialYearId)}
              isPinned={hoverPreview.isPinned}
              onMouseEnter={keepHoverPreviewOpen}
              onMouseLeave={scheduleHoverPreviewClose}
              onTogglePin={toggleHoverPreviewPin}
              onOpenFull={openHoverPreviewFull}
              onClose={closeHoverPreview}
            />
          );
        })()}

      <DocumentPreview
        documentId={previewDocId}
        isOpen={!!previewDocId}
        onClose={() => setPreviewDocId(null)}
        profiles={allProfiles}
        financialYears={years}
      />

      {/* Edit Profile */}
      {canWrite && (
        <ModalDrawer
          isOpen={editingProfile}
          onClose={() => setEditingProfile(false)}
          title="Edit Profile"
        >
          <ProfileForm
            profile={profile}
            onSave={async p => {
              await getRepository().saveProfile(p);
              setEditingProfile(false);
              loadData();
            }}
            onCancel={() => setEditingProfile(false)}
          />
        </ModalDrawer>
      )}

      {/* Add/Edit Reference Block */}
      {canWrite && (
        <ModalDrawer
          isOpen={isAddingBlock || !!editingBlock}
          onClose={() => {
            setIsAddingBlock(false);
            setEditingBlock(null);
          }}
          title={editingBlock ? 'Edit Reference Block' : 'Add Reference Block'}
          heightMode="content"
        >
          <ReferenceBlockForm
            block={editingBlock}
            profileId={profile.id}
            onSave={async b => {
              await getRepository().saveReferenceBlock(b);
              setIsAddingBlock(false);
              setEditingBlock(null);
              loadData();
            }}
            onCancel={() => {
              setIsAddingBlock(false);
              setEditingBlock(null);
            }}
          />
        </ModalDrawer>
      )}

      {/* Add Financial Year */}
      {canWrite && (
        <ModalDrawer
          isOpen={isAddingYear}
          onClose={() => setIsAddingYear(false)}
          title="Add Financial Year"
        >
          <FinancialYearForm
            profileId={profile.id}
            onSave={async y => {
              await getRepository().saveFinancialYear(y);
              setIsAddingYear(false);
              loadData();
            }}
            onSaveWithDocuments={handleSaveWithDocuments}
            onCancel={() => setIsAddingYear(false)}
          />
        </ModalDrawer>
      )}
    </div>
  );
}